import axios from "axios";
import * as React from "react";
import { store } from "../../store";
import config from "../../apiConfig";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { LikeFilled } from "./LikeFilled";
import { Progress, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getAgents } from "../../actions/agentsAction";
import FeedbackIllust from "../../assets/images/feedback_illust.svg"
import { LoadingOutlined } from "@ant-design/icons";
import CustomDatePicker from "./CustomDatePicker";

const StyledCard = styled.div`
  cursor: pointer;
  width: 310px;
  height: 200px;
  padding: 16px 20px 20px;
  border: 1px solid #E5E5E5;
  border-radius: 20px;
  box-shadow: 0px 9px 14px 0px #CDD3E033;
`;

type Agent = {
    botHandle: string;
    feedbackCount: number;
    upvotes: number;
    downvotes: number;
};

const Card = ({ title, upvote, downvote }: { title: string; upvote: number, downvote: number }) => {
    const successPercent = Number(((upvote / (upvote + downvote)) * 100).toFixed(1));

    return (
        <StyledCard>
            <span style={{ color: "#121212", fontSize: "20px", fontWeight: 600 }}>{title}</span>
            <div style={{ display: "flex", justifyContent: "space-evenly", height: 56, paddingTop: 5, background: "#F6F8FF", borderRadius: 6, margin: "8px 0px" }}>
                <div>
                    <span style={{ color: "#7D7D7D" }}>Feedback</span>
                    <p style={{ color: "#121212", fontWeight: 500 }}>{upvote + downvote}</p>
                </div>
                <div style={{ borderLeft: "2px dashed #D9DEF1", height: 40, marginTop: 4 }} />
                <div>
                    <span style={{ color: "#7D7D7D" }}>Success Rate</span>
                    <p style={{ color: "#121212", fontWeight: 500 }}>{successPercent}%</p>
                </div>
            </div>
            <Progress percent={100} successPercent={successPercent} strokeColor="#FF8933" showInfo={false} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#36BF71" }}>{upvote}</span>
                <span style={{ color: "#FF8933" }}>{downvote}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-evenly", color: "#7D7D7D" }}>
                <div>
                    <LikeFilled style={{ marginRight: 6 }} solid={true} fillColor="#36BF71" strokeColor="#aee5c6" />
                    Upvote
                </div>
                <div>
                    <LikeFilled style={{ transform: "rotate(180deg)", marginRight: 6 }} solid={true} fillColor="#FF8933" strokeColor="#ffd0ad" />
                    Downvote
                </div>
            </div>
        </StyledCard>
    );
};

const array2Objects = (array = [], field) => {
    return array.reduce((a, c) => {
        a[c[field]] = c;
        return a;
    }, {});
};

const Dashboard = () => {
    const [feedbackData, setFeedbackData] = React.useState<any>(null);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const agents = useSelector((state: any) => state.agent.agents);
    const {startDate, endDate} = useSelector((state: any) => state.ui.feedbackFilter);
    const botNames = array2Objects(agents, "botHandle");
    const dispatch = useDispatch();
    const fetchAgents = React.useCallback(() => dispatch(getAgents()), [dispatch]);
    const [stats, setStats] = React.useState({
        successPercent: 0,
        feedbackCount: 0,
        downvotes: 0,
        upvotes: 0
    });

    React.useEffect(() => {
        if (!agents.length)
            fetchAgents();
    }, []);

    const getSummary = async () => {
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${store.getState().auth.info.accessToken}`
            }
        };
        const params = new URLSearchParams();
        if (startDate) params.append("startTime", startDate);
        if (endDate) params.append("endTime", endDate);

        const url = `${config.baseUrl}${config.org}${config.v1}/feedbackSummary${params.toString() ? `?${params.toString()}` : ""}`;
        setIsLoading(true);
        try {
            let response = await axios(url, options);
            if (response.data.results.length) {
                const stats = response.data.results.reduce(
                    (acc, curr) => {
                        acc.feedbackCount += curr.feedbackCount;
                        acc.downvotes += curr.downvotes;
                        acc.upvotes += curr.upvotes;
                        return acc;
                    },
                    { feedbackCount: 0, downvotes: 0, upvotes: 0 }
                );
                const successPercent = Number(((stats.upvotes / stats.feedbackCount) * 100).toFixed(1));
                setStats({
                    successPercent: successPercent,
                    feedbackCount: stats.feedbackCount,
                    downvotes: stats.downvotes,
                    upvotes: stats.upvotes
                });
            } else {
                setStats({
                    successPercent: 0,
                    feedbackCount: 0,
                    downvotes: 0,
                    upvotes: 0
                });
            }
            return response.data;
        } catch (error) {
            setIsLoading(true);
        }
    };

    React.useEffect(() => {
        (async () => {
            const data = await getSummary();
            if (data) {
                setFeedbackData(data.results);
                setIsLoading(false);
            }
        })();
    }, [startDate, endDate])

    return (
        <div style={{ padding: "24px 38px" }}>
            <header style={{ display: "flex", gap: 24, marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontWeight: 500 }}>
                    Dashboard
                </h2>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <CustomDatePicker/>
                </div>
            </header>
            {isLoading ?
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} />
                </div> :
                <>
                    <div style={{ display: "flex", justifyContent: "space-evenly", width: "38%", height: "140px", boxShadow: "0px 9px 14px 0px #CDD3E026", borderRadius: "20px", border: "1px solid #ECEEF6" }}>
                        <div style={{ padding: "30px 24px" }}>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="14" cy="14" r="14" fill="#E7E1FF" />
                                <path d="M10.9866 18.2332L13.0533 19.8331C13.32 20.0998 13.92 20.2331 14.32 20.2331H16.8533C17.6533 20.2331 18.52 19.6331 18.72 18.8331L20.32 13.9665C20.6533 13.0331 20.0533 12.2331 19.0533 12.2331H16.3866C15.9866 12.2331 15.6533 11.8998 15.72 11.4331L16.0533 9.29982C16.1866 8.69982 15.7866 8.03315 15.1866 7.83315C14.6533 7.63315 13.9866 7.89982 13.72 8.29982L10.9866 12.3665" stroke="#292D32" stroke-miterlimit="10" />
                                <path d="M7.58667 18.2331V11.6998C7.58667 10.7664 7.98667 10.4331 8.92 10.4331H9.58667C10.52 10.4331 10.92 10.7664 10.92 11.6998V18.2331C10.92 19.1664 10.52 19.4998 9.58667 19.4998H8.92C7.98667 19.4998 7.58667 19.1664 7.58667 18.2331Z" stroke="#292D32" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <span style={{ marginLeft: 8 }}>Total Feedback</span>
                            <p style={{ fontSize: 28, color: "#121212", fontWeight: 600, padding: "10px 18px", textAlign: "center" }}>{stats.feedbackCount}</p>
                        </div>
                        <div style={{ borderLeft: "2px dashed #D9DEF1", height: "100px", margin: 24 }} />
                        <div style={{ padding: "30px 24px" }}>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="14" cy="14" r="14" transform="rotate(180 14 14)" fill="#FFC400" fill-opacity="0.3" />
                                <path d="M15.1533 8.34025L16.3267 10.6869C16.4867 11.0136 16.9133 11.3269 17.2733 11.3869L19.4 11.7403C20.76 11.9669 21.08 12.9536 20.1 13.9269L18.4467 15.5803C18.1667 15.8603 18.0133 16.4003 18.1 16.7869L18.5733 18.8336C18.9467 20.4536 18.0867 21.0803 16.6533 20.2336L14.66 19.0536C14.3 18.8403 13.7067 18.8403 13.34 19.0536L11.3467 20.2336C9.92 21.0803 9.05333 20.4469 9.42667 18.8336L9.9 16.7869C9.98667 16.4003 9.83333 15.8603 9.55333 15.5803L7.9 13.9269C6.92667 12.9536 7.24 11.9669 8.6 11.7403L10.7267 11.3869C11.08 11.3269 11.5067 11.0136 11.6667 10.6869L12.84 8.34025C13.48 7.06692 14.52 7.06692 15.1533 8.34025Z" stroke="#292D32" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <span style={{ marginLeft: 8 }}>Success Rate</span>
                            <p style={{ fontSize: 28, color: "#121212", fontWeight: 600, padding: "10px 18px", textAlign: "center" }}>{stats.successPercent}%</p>
                        </div>
                    </div>
                    <img style={{ position: "absolute", right: 50, top: 20 }} src={FeedbackIllust} alt="feedback_illust" />
                    {feedbackData.length > 0 && Object.keys(botNames).length > 0 &&
                        <div style={{ marginTop: 35 }}>
                            <div style={{ display: "flex", gap: 28, flexWrap: "wrap", overflow: "auto", height: "44vh", paddingBottom: 10 }}>
                                {feedbackData.sort((a, b) => b.feedbackCount - a.feedbackCount).map((bot: Agent) => {
                                    return <Link to={`/feedback/bot/${bot.botHandle}`}
                                        key={botNames[bot.botHandle].name}
                                        style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                        <Card
                                            title={botNames[bot.botHandle].name}
                                            upvote={bot.upvotes}
                                            downvote={bot.downvotes} />
                                    </Link>
                                })
                                }
                            </div>
                        </div>
                    }
                </>}
        </div>
    )
}

export default Dashboard